import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Alert } from 'reactstrap'
import AutoTableLayout from './AutoTableLayout'
import Json2csvParser from 'json2csv'
import { saveAs } from 'file-saver'
import { getNestedObject } from '../../lib/util'

class AutoTable extends React.Component {
  static propTypes = {
    bordered: PropTypes.bool,
    borderless: PropTypes.bool,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        fieldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
          .isRequired,
        sort: PropTypes.bool,
        filter: PropTypes.bool,
        sortValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        filterValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        headerClassName: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.func,
        ]),
        cellClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        cellOnClick: PropTypes.func,
      }),
    ).isRequired,
    dark: PropTypes.bool,
    defaultFilter: PropTypes.arrayOf(
      PropTypes.shape({
        columnTitle: PropTypes.string.isRequired,
        query: PropTypes.string.isRequired,
      }),
    ),
    defaultSort: PropTypes.shape({
      columnTitle: PropTypes.string.isRequired,
      direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
    }),
    export: PropTypes.bool,
    filter: PropTypes.bool,
    hover: PropTypes.bool,
    rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    rowOnClick: PropTypes.func,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    size: PropTypes.string,
    sort: PropTypes.bool,
    striped: PropTypes.bool,
    title: PropTypes.string,
  }
  static defaultProps = {
    sort: false,
    export: false,
    filter: false,
    defaultSort: {
      columnTitle: '',
      direction: 'asc',
    },
    defaultFilter: [],
  }

  constructor(props) {
    super(props)

    this.state = {
      id: _.uniqueId('autotable_'),
      sortData: { ...props.defaultSort },
      filterData: [...props.defaultFilter],
    }
  }

  onToggleSort = columnTitle => {
    const sortData = { ...this.state.sortData }
    let { direction } = sortData || {}
    direction = direction === 'asc' ? 'desc' : 'asc'
    this.setState({ sortData: { columnTitle, direction } })
  }

  updateFilter = (columnTitle, query) => {
    const filterData = [...this.state.filterData]
    let found = false
    for (let i = 0; i < filterData.length; i++) {
      if (filterData[i].columnTitle === columnTitle) {
        filterData[i].query = query
        found = true
        break
      }
    }
    if (!found) filterData.push({ columnTitle: columnTitle, query: query })
    this.setState({ filterData })
  }

  getRows = () => {
    const { sortData, filterData } = this.state
    const { columns, rows = [] } = this.props
    const enableFilter = this.props.filter && rows.length > 1
    const enableSort = this.props.sort && rows.length > 1
    if (rows.length === 0) return []
    let result = []
    if (enableFilter && filterData.length > 0) {
      result = _.cloneDeep(rows) // don't mutate the rows collection
      result = this.filterRows(result)
    } else {
      result = rows
    }
    if (enableSort && sortData.columnTitle) {
      const col = columns.find(c => c.title === sortData.columnTitle)
      if (!col) return result
      const sortingValue = row => this.resolveSortValue(col, row)
      result = _.orderBy(result, sortingValue, [sortData.direction])
    }
    return result || []
  }

  filterRows = rows => {
    const { filterData } = this.state
    for (let i = 0; i < filterData.length; i++) {
      rows = this.filterApply(filterData[i], rows)
    }
    return rows
  }

  filterApply = (filter, rows) => {
    if (!filter.columnTitle && !filter.query) return rows
    const { columns } = this.props
    const column = columns.find(c => c.title === filter.columnTitle)
    if (column) {
      const expression = new RegExp(RegExp.escape(filter.query), 'i')
      return _.filter(rows, row => {
        const filterValue = this.resolveFilterValue(column, row)
        const match = expression.test(filterValue)
        return match
      })
    }
    return rows
  }

  resolveClassName = (className, datum) => {
    try {
      const result =
        typeof className === 'function' ? className(datum) : className
      return result || ''
    } catch (err) {
      // Error interpreting className - silently continue
    }
  }

  resolveCellValue = (column, datum) => {
    try {
      const { fieldValue } = column
      const result =
        typeof fieldValue === 'function'
          ? fieldValue(datum)
          : getNestedObject(datum, fieldValue)
      if (typeof result === 'string') return result || ''
      else return result
    } catch (err) {
      // Error interpreting fieldValue - silently continue
    }
  }

  resolveFilterValue = (column, datum) => {
    try {
      const { filterValue } = column
      let filterResult
      if (!filterValue) {
        filterResult = this.resolveCellValue(column, datum)
      } else {
        filterResult =
          typeof filterValue === 'function'
            ? filterValue(datum)
            : datum[filterValue]
      }

      return typeof filterResult === 'string' ||
        typeof filterResult === 'number'
        ? filterResult
        : ''
    } catch (err) {
      // Error interpreting filterValue - silently continue
    }
  }

  resolveSortValue = (column, datum) => {
    try {
      const { sortValue } = column
      if (!sortValue) return this.resolveCellValue(column, datum)

      const result =
        typeof sortValue === 'function' ? sortValue(datum) : datum[sortValue]
      if (typeof result === 'string') return result || ''
      else return result
    } catch (err) {
      // Error interpreting sortValue - silently continue
    }
  }

  exportToCsv = () => {
    try {
      const csv = Json2csvParser.parse(this.getRows())
      const blob = new Blob([csv], {
        type: 'text/plain;charset=utf-8',
      })
      saveAs(blob, `${this.props.title}.csv`)
    } catch (err) {
      // Error exporting to CSV - silently continue
    }
  }

  render = () => {
    const styles = {
      dark: this.props.dark,
      bordered: this.props.bordered,
      borderless: this.props.borderless,
      size: this.props.size,
      hover: this.props.hover,
      striped: this.props.striped,
    }
    const { dark, columns = [], filter, rows } = this.props
    const theadClassName = dark ? 'thead-dark' : 'thead-light'
    const enableFilter = filter && rows.length > 1
    const sortedRows = this.getRows()
    if (columns.length === 0)
      return <Alert color="warning">No columns specified</Alert>

    return (
      <AutoTableLayout
        columns={columns}
        enableFilter={enableFilter}
        export={this.props.export}
        exportToCsv={this.exportToCsv}
        filterData={this.state.filterData}
        id={this.state.id}
        onToggleSort={this.onToggleSort}
        resolveCellValue={this.resolveCellValue}
        resolveClassName={this.resolveClassName}
        rowClassName={this.props.rowClassName}
        rowOnClick={this.props.rowOnClick}
        sort={this.props.sort}
        sortData={this.state.sortData}
        sortedRows={sortedRows}
        styles={styles}
        theadClassName={theadClassName}
        title={this.props.title}
        updateFilter={this.updateFilter}
      />
    )
  }
}
export default AutoTable
